import React, { useState } from 'react';
import axios from 'axios';

// Переводы
const TRANSLATIONS = {
  ru: {
    title: "AI Генерация (18+)",
    subtitle: "Создавайте изображения и видео с помощью искусственного интеллекта",
    generateImage: "Генерировать изображение",
    generateVideo: "Генерировать видео",
    imageTab: "Изображение",
    videoTab: "Видео",
    promptPlaceholder: "Опишите, что вы хотите создать...",
    imageUrlPlaceholder: "URL изображения или загрузите файл",
    uploadImage: "Загрузить изображение",
    generating: "Генерация...",
    processing: "Обработка...",
    success: "Успешно!",
    error: "Ошибка",
    payWithStars: "Оплатить {price} ⭐",
    imagePrice: "9",
    videoPrice: "49",
    resultTitle: "Результат:",
    download: "Скачать",
    tryAgain: "Попробовать снова",
    selectTab: "Выберите тип генерации:"
  },
  en: {
    title: "AI Generation (18+)",
    subtitle: "Create images and videos with artificial intelligence",
    generateImage: "Generate Image",
    generateVideo: "Generate Video",
    imageTab: "Image",
    videoTab: "Video",
    promptPlaceholder: "Describe what you want to create...",
    imageUrlPlaceholder: "Image URL or upload file",
    uploadImage: "Upload Image",
    generating: "Generating...",
    processing: "Processing...",
    success: "Success!",
    error: "Error",
    payWithStars: "Pay {price} ⭐",
    imagePrice: "9",
    videoPrice: "49",
    resultTitle: "Result:",
    download: "Download",
    tryAgain: "Try Again",
    selectTab: "Select generation type:"
  }
};

function AIGeneration({ user, language = 'ru' }) {
  const [activeTab, setActiveTab] = useState('image');
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState(null); // 'processing', 'completed', 'failed', 'waiting_payment'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);

  const t = (key) => TRANSLATIONS[language]?.[key] || TRANSLATIONS.ru[key];

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Для простоты используем FileReader для создания data URL
      // В реальном проекте лучше загружать на сервер
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const createInvoice = async (type) => {
    try {
      const response = await axios.post('/create_generation_invoice', {
        initData: user.initData,
        type: type
      });
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      setError(language === 'ru' ? 'Введите описание изображения' : 'Enter image description');
      return;
    }

    setError(null);
    setStatus('waiting_payment');
    setResult(null);
    setGenerating(true);

    try {
      // Сохраняем данные для генерации
      const generationData = {
        type: 'image',
        prompt: prompt,
        size: '1280*720',
        seed: -1
      };

      // Создаем invoice для оплаты
      const invoice = await createInvoice('image');
      
      if (!invoice || !invoice.success) {
        throw new Error(invoice?.message || 'Failed to create invoice');
      }
      
      // Показываем понятное сообщение пользователю
      const message = language === 'ru' 
        ? '💳 Invoice отправлен в ваш чат с ботом!\n\n📱 Сейчас откроется Telegram - найдите сообщение с invoice и оплатите.\n\n✅ После оплаты генерация начнется автоматически.'
        : '💳 Invoice sent to your bot chat!\n\n📱 Telegram will open now - find the invoice message and pay.\n\n✅ After payment, generation will start automatically.';
      
      // Показываем сообщение перед открытием Telegram
      if (window.confirm(message)) {
        // Открываем чат с ботом
        if (window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink('https://t.me/hotbump_bot');
        } else {
          window.open('https://t.me/hotbump_bot', '_blank');
        }
      }
      
      // Начинаем проверку оплаты
      pollPaymentStatus(invoice.payload, generationData);
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError(error.response?.data?.detail || (language === 'ru' ? 'Ошибка создания invoice' : 'Error creating invoice'));
      setStatus('failed');
      setGenerating(false);
    }
  };

  const generateVideo = async () => {
    if (!imageUrl.trim()) {
      setError(language === 'ru' ? 'Введите URL изображения или загрузите файл' : 'Enter image URL or upload file');
      return;
    }
    if (!prompt.trim()) {
      setError(language === 'ru' ? 'Введите описание для видео' : 'Enter video description');
      return;
    }

    setError(null);
    setStatus('waiting_payment');
    setResult(null);
    setGenerating(true);

    try {
      // Сохраняем данные для генерации
      const generationData = {
        type: 'video',
        image: imageUrl,
        prompt: prompt,
        duration: 5,
        seed: -1
      };

      // Создаем invoice для оплаты
      const invoice = await createInvoice('video');
      
      if (!invoice || !invoice.success) {
        throw new Error(invoice?.message || 'Failed to create invoice');
      }
      
      // Показываем понятное сообщение пользователю
      const message = language === 'ru' 
        ? '💳 Invoice отправлен в ваш чат с ботом!\n\n📱 Сейчас откроется Telegram - найдите сообщение с invoice и оплатите.\n\n✅ После оплаты генерация начнется автоматически.'
        : '💳 Invoice sent to your bot chat!\n\n📱 Telegram will open now - find the invoice message and pay.\n\n✅ After payment, generation will start automatically.';
      
      // Показываем сообщение перед открытием Telegram
      if (window.confirm(message)) {
        // Открываем чат с ботом
        if (window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink('https://t.me/hotbump_bot');
        } else {
          window.open('https://t.me/hotbump_bot', '_blank');
        }
      }
      
      // Начинаем проверку оплаты
      pollPaymentStatus(invoice.payload, generationData);
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError(error.response?.data?.detail || (language === 'ru' ? 'Ошибка создания invoice' : 'Error creating invoice'));
      setStatus('failed');
      setGenerating(false);
    }
  };

  const pollPaymentStatus = async (payload, generationData) => {
    const maxAttempts = 120; // Максимум 2 минуты
    let attempts = 0;

    const checkPayment = async () => {
      try {
        const response = await axios.get('/check_generation_payment', {
          params: {
            initData: user.initData,
            payload: payload
          }
        });

        if (response.data.success && response.data.paid) {
          // Оплата подтверждена, начинаем генерацию
          setStatus('processing');
          startGeneration(generationData);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkPayment, 1000);
        } else {
          setStatus('failed');
          setError(language === 'ru' ? 'Превышено время ожидания оплаты' : 'Payment timeout');
          setGenerating(false);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkPayment, 2000);
        } else {
          setStatus('failed');
          setError(language === 'ru' ? 'Ошибка проверки оплаты' : 'Payment check error');
          setGenerating(false);
        }
      }
    };

    checkPayment();
  };

  const startGeneration = async (generationData) => {
    try {
      let response;
      if (generationData.type === 'image') {
        response = await axios.post('/generate_image', {
          initData: user.initData,
          prompt: generationData.prompt,
          size: generationData.size,
          seed: generationData.seed
        });
      } else {
        response = await axios.post('/generate_video', {
          initData: user.initData,
          image: generationData.image,
          prompt: generationData.prompt,
          duration: generationData.duration,
          seed: generationData.seed
        });
      }

      if (response.data.success) {
        setRequestId(response.data.request_id);
        pollStatus(response.data.request_id);
      }
    } catch (error) {
      console.error('Error starting generation:', error);
      setError(error.response?.data?.detail || (language === 'ru' ? 'Ошибка генерации' : 'Generation error'));
      setStatus('failed');
      setGenerating(false);
    }
  };

  const pollStatus = async (reqId) => {
    const maxAttempts = 120; // Максимум 2 минуты (каждые 1 секунду)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await axios.post('/check_generation_status', {
          initData: user.initData,
          request_id: reqId
        });

        if (response.data.success) {
          const status = response.data.status;
          
          if (status === 'completed') {
            setStatus('completed');
            setResult(response.data.outputs[0]);
            setGenerating(false);
            return;
          } else if (status === 'failed') {
            setStatus('failed');
            setError(response.data.error || 'Генерация не удалась');
            setGenerating(false);
            return;
          } else {
            // Продолжаем проверку
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 1000);
            } else {
              setStatus('failed');
              setError('Превышено время ожидания');
              setGenerating(false);
            }
          }
        }
      } catch (error) {
        console.error('Error checking status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('failed');
          setError('Ошибка проверки статуса');
          setGenerating(false);
        }
      }
    };

    checkStatus();
  };

  const resetForm = () => {
    setPrompt('');
    setImageUrl('');
    setUploadedImage(null);
    setStatus(null);
    setResult(null);
    setError(null);
    setRequestId(null);
    setGenerating(false);
  };

  return (
    <div className="bg-dark-800 border border-burgundy-900 rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-100 mb-2">{t('title')}</h2>
      <p className="text-gray-400 mb-6">{t('subtitle')}</p>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => {
            setActiveTab('image');
            resetForm();
          }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'image'
              ? 'bg-burgundy-700 text-white'
              : 'bg-dark-700 text-gray-400 hover:text-white'
          }`}
        >
          {t('imageTab')}
        </button>
        <button
          onClick={() => {
            setActiveTab('video');
            resetForm();
          }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'video'
              ? 'bg-burgundy-700 text-white'
              : 'bg-dark-700 text-gray-400 hover:text-white'
          }`}
        >
          {t('videoTab')}
        </button>
      </div>

      {/* Image Generation Form */}
      {activeTab === 'image' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('promptPlaceholder')}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('promptPlaceholder')}
              className="w-full bg-dark-900 border border-burgundy-700 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-burgundy-600"
              rows={4}
              disabled={generating}
            />
          </div>

          {!generating && !result && (
            <button
              onClick={generateImage}
              disabled={!prompt.trim()}
              className="w-full bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-burgundy-500/50"
            >
              {t('generateImage')} ({t('imagePrice')} ⭐)
            </button>
          )}

          {generating && status === 'waiting_payment' && (
            <div className="text-center py-4">
              <div className="animate-pulse text-burgundy-400 mb-2">💳</div>
              <p className="text-gray-400">{language === 'ru' ? 'Ожидание оплаты... Проверьте Telegram' : 'Waiting for payment... Check Telegram'}</p>
            </div>
          )}

          {generating && status === 'processing' && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy-500 mx-auto mb-2"></div>
              <p className="text-gray-400">{t('generating')}</p>
            </div>
          )}

          {result && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-100 mb-2">{t('resultTitle')}</h3>
              <img
                src={result}
                alt="Generated"
                className="w-full rounded-lg mb-4"
              />
              <div className="flex space-x-2">
                <a
                  href={result}
                  download
                  className="flex-1 bg-burgundy-700 hover:bg-burgundy-600 text-white font-bold py-2 px-4 rounded-lg transition-colors text-center"
                >
                  {t('download')}
                </a>
                <button
                  onClick={resetForm}
                  className="flex-1 bg-dark-700 hover:bg-dark-600 text-gray-300 font-bold py-2 px-4 rounded-lg transition-colors"
                >
                  {t('tryAgain')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Video Generation Form */}
      {activeTab === 'video' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('imageUrlPlaceholder')}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder={t('imageUrlPlaceholder')}
                className="flex-1 bg-dark-900 border border-burgundy-700 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-burgundy-600"
                disabled={generating}
              />
              <label className="bg-dark-700 hover:bg-dark-600 text-gray-300 font-medium py-3 px-4 rounded-lg cursor-pointer transition-colors">
                {t('uploadImage')}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={generating}
                />
              </label>
            </div>
            {uploadedImage && (
              <img
                src={uploadedImage}
                alt="Preview"
                className="mt-2 w-full max-w-xs rounded-lg"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('promptPlaceholder')}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('promptPlaceholder')}
              className="w-full bg-dark-900 border border-burgundy-700 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-burgundy-600"
              rows={4}
              disabled={generating}
            />
          </div>

          {!generating && !result && (
            <button
              onClick={generateVideo}
              disabled={!imageUrl.trim() || !prompt.trim()}
              className="w-full bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-burgundy-500/50"
            >
              {t('generateVideo')} ({t('videoPrice')} ⭐)
            </button>
          )}

          {generating && status === 'waiting_payment' && (
            <div className="text-center py-4">
              <div className="animate-pulse text-burgundy-400 mb-2">💳</div>
              <p className="text-gray-400">{language === 'ru' ? 'Ожидание оплаты... Проверьте Telegram' : 'Waiting for payment... Check Telegram'}</p>
            </div>
          )}

          {generating && status === 'processing' && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy-500 mx-auto mb-2"></div>
              <p className="text-gray-400">{t('generating')}</p>
            </div>
          )}

          {result && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-100 mb-2">{t('resultTitle')}</h3>
              <video
                src={result}
                controls
                className="w-full rounded-lg mb-4"
              />
              <div className="flex space-x-2">
                <a
                  href={result}
                  download
                  className="flex-1 bg-burgundy-700 hover:bg-burgundy-600 text-white font-bold py-2 px-4 rounded-lg transition-colors text-center"
                >
                  {t('download')}
                </a>
                <button
                  onClick={resetForm}
                  className="flex-1 bg-dark-700 hover:bg-dark-600 text-gray-300 font-bold py-2 px-4 rounded-lg transition-colors"
                >
                  {t('tryAgain')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-burgundy-900 bg-opacity-50 border border-burgundy-600 text-burgundy-400 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
}

export default AIGeneration;

